import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Dashboard Store
 *
 * Manages user preferences for dashboard sections:
 * - Section visibility (hide/show)
 * - Section collapse state (expanded/collapsed)
 * - Section order (drag-and-drop reordering)
 * - Persists to localStorage per team
 *
 * **Team-scoped Storage:**
 * The store uses a factory pattern to create team-specific instances.
 * Each team gets its own localStorage key: `dashboard-sections-${teamId}`
 *
 * **Row-based model:**
 * Each entry represents a full dashboard row (which may contain multiple
 * visual columns internally). Drag-and-drop reorders entire rows.
 *
 * Row IDs:
 *   'release-reviewer'  → Release sections for reviewers (waiting for your release, ...)
 *   'release-operator'  → Release sections for operators (ready to schedule, changes requested)
 *   'published-worked'  → Row 1: Published (2/3) + Something That Worked (1/3)
 *   'goals'             → Row 2: Weekly Goals (full width)
 *   'trends'            → Row 3: Industry Trends (full width)
 *   'remix'             → Row 4: Remix - Quick Sendout Opportunities (full width)
 *   'ai-profiles'       → Row 5: AI Brand Profiles (full width)
 *   'pipeline'          → Row 6: Section title + Stats row (full width)
 *   'mapped-publishing' → Row 7: Mapped (50%) + Publishing (50%)
 *
 * **Usage:**
 * ```typescript
 * const { teamId } = useEffectiveTeamId();
 * const { sections, toggleCollapse, reorder } = useDashboardStore(teamId);
 * ```
 */

export interface DashboardSection {
  id: string;
  visible: boolean;
  collapsed: boolean;
  order: number;
}

interface DashboardStore {
  sections: DashboardSection[];
  toggleVisibility: (sectionId: string) => void;
  toggleCollapse: (sectionId: string) => void;
  reorder: (activeId: string, overId: string) => void;
  reset: () => void;
  getSortedSections: () => DashboardSection[];
}

const DEFAULT_SECTIONS: DashboardSection[] = [
  { id: 'published-worked', visible: true, collapsed: false, order: 0 },  // Daily Brief: paired 2/3+1/3
  { id: 'pipeline', visible: true, collapsed: false, order: 1 },          // Your Asset Pipeline title + stats
  { id: 'release-reviewer', visible: true, collapsed: false, order: 2 },  // Release: reviewer sections (auto-hidden unless the viewer is a reviewer)
  { id: 'release-operator', visible: true, collapsed: false, order: 3 },  // Release: operator sections (auto-hidden unless the viewer is an operator)
  { id: 'goals', visible: true, collapsed: false, order: 4 },             // Weekly Goals
  { id: 'trends', visible: true, collapsed: false, order: 5 },            // Industry Trends
  { id: 'remix', visible: true, collapsed: false, order: 6 },             // Remix
  { id: 'ai-profiles', visible: true, collapsed: false, order: 7 },       // AI Profiles
  { id: 'ai-insights', visible: true, collapsed: false, order: 8 },       // AI Insights (auto-hidden when empty)
  { id: 'mapped-publishing', visible: true, collapsed: false, order: 9 }, // Mapped & Ready + Publishing, paired 50/50
];

/**
 * Relative order of the core rows in the old un-customised default. Rows added later were
 * appended/prepended by the old merge at varying positions (ai-insights, release rows), so only
 * the core rows' relative order tells whether a saved layout was ever rearranged by the user.
 */
const LEGACY_CORE_ORDER: readonly string[] = [
  'published-worked', 'goals', 'trends', 'remix', 'ai-profiles', 'pipeline', 'mapped-publishing',
];

const RELEASE_ROW_IDS: readonly string[] = ['release-reviewer', 'release-operator'];

/**
 * Merge a persisted layout with the current defaults. Pure, exported for testing.
 * - Customised layouts keep their order; missing rows are added (release rows right after
 *   'pipeline', or at the end if it is absent; any other new row at the end).
 * - Layouts whose core rows are still in the old default relative order move to the new default (keeping visible/collapsed flags).
 */
export const mergeDashboardSections = (stored: DashboardSection[] | undefined): DashboardSection[] => {
  if (!stored || !Array.isArray(stored)) return DEFAULT_SECTIONS;
  // Pre-row-model IDs: reset to defaults.
  if (!stored.some((s) => s.id === 'published-worked')) return DEFAULT_SECTIONS;

  const sorted = [...stored].sort((a, b) => a.order - b.order);
  const ids = sorted.map((s) => s.id);

  // Core rows only: drop release rows, ai-insights and any id unknown to the legacy default
  const coreIds = ids.filter((id) => LEGACY_CORE_ORDER.includes(id));
  const isUncustomised =
    coreIds.length === LEGACY_CORE_ORDER.length && LEGACY_CORE_ORDER.every((id, i) => id === coreIds[i]);
  if (isUncustomised) {
    const byId = new Map(sorted.map((s) => [s.id, s]));
    return DEFAULT_SECTIONS.map((d) => {
      const prev = byId.get(d.id);
      return prev ? { ...d, visible: prev.visible, collapsed: prev.collapsed } : d;
    });
  }

  const storedIds = new Set(ids);
  const missing = DEFAULT_SECTIONS.filter((d) => !storedIds.has(d.id));
  if (missing.length === 0) return stored;

  const result: DashboardSection[] = [...sorted];
  const missingRelease = missing.filter((d) => RELEASE_ROW_IDS.includes(d.id));
  const missingOther = missing.filter((d) => !RELEASE_ROW_IDS.includes(d.id));
  const pipelineIdx = result.findIndex((s) => s.id === 'pipeline');
  if (missingRelease.length > 0) {
    if (pipelineIdx === -1) result.push(...missingRelease);
    else result.splice(pipelineIdx + 1, 0, ...missingRelease);
  }
  result.push(...missingOther);
  return result.map((s, i) => ({ ...s, order: i }));
};

/**
 * Store cache to avoid creating duplicate instances for the same team
 */
const storeCache = new Map<string, ReturnType<typeof createDashboardStore>>();

/**
 * Creates a dashboard store instance for a specific team
 * @param teamId - The team ID to scope storage to
 * @returns Zustand store instance
 */
const createDashboardStore = (teamId: number | null) => {
  return create<DashboardStore>()(
    persist(
      (set, get) => ({
        sections: DEFAULT_SECTIONS,

        /**
         * Toggle visibility of a section/row
         * @param sectionId - The row ID to toggle
         */
        toggleVisibility: (sectionId: string) =>
          set((state) => ({
            sections: state.sections.map((section) =>
              section.id === sectionId
                ? { ...section, visible: !section.visible }
                : section
            ),
          })),

        /**
         * Toggle collapsed state of a section/row
         * @param sectionId - The row ID to toggle
         */
        toggleCollapse: (sectionId: string) =>
          set((state) => ({
            sections: state.sections.map((section) =>
              section.id === sectionId
                ? { ...section, collapsed: !section.collapsed }
                : section
            ),
          })),

        /**
         * Reorder rows via drag-and-drop
         * Swaps the order values of the active and over items
         * @param activeId - The row being dragged
         * @param overId - The row being dragged over
         */
        reorder: (activeId: string, overId: string) => {
          if (activeId === overId) return;

          set((state) => {
            const sections = [...state.sections];
            const activeIndex = sections.findIndex((s) => s.id === activeId);
            const overIndex = sections.findIndex((s) => s.id === overId);

            if (activeIndex === -1 || overIndex === -1) return state;

            // Swap order values
            const activeOrder = sections[activeIndex].order;
            const overOrder = sections[overIndex].order;

            sections[activeIndex] = { ...sections[activeIndex], order: overOrder };
            sections[overIndex] = { ...sections[overIndex], order: activeOrder };

            return { sections };
          });
        },

        /**
         * Reset all sections to default configuration
         */
        reset: () => set({ sections: DEFAULT_SECTIONS }),

        /**
         * Get sections sorted by order, filtered by visible
         * @returns Sorted array of visible sections
         */
        getSortedSections: () => {
          const { sections } = get();
          return sections
            .filter((section) => section.visible)
            .sort((a, b) => a.order - b.order);
        },
      }),
      {
        name: `dashboard-sections-${teamId || 'default'}`,
        merge: (persisted, current) => {
          const stored = persisted as Partial<DashboardStore> | undefined;
          return { ...current, ...stored, sections: mergeDashboardSections(stored?.sections) };
        },
      }
    )
  );
};

/**
 * useDashboardStore Hook
 *
 * Returns a dashboard store instance scoped to the given team.
 * Instances are cached to ensure the same store is returned for the same team.
 *
 * **Important:** Pass the effective team ID from `useEffectiveTeamId()` to ensure
 * correct storage scoping when in God Mode.
 *
 * @param teamId - The team ID to scope the store to (from useEffectiveTeamId)
 * @returns Dashboard store instance
 *
 * @example
 * ```typescript
 * const { teamId } = useEffectiveTeamId();
 * const {
 *   sections,
 *   toggleVisibility,
 *   toggleCollapse,
 *   reorder,
 *   getSortedSections
 * } = useDashboardStore(teamId);
 *
 * // Get sorted visible sections
 * const visibleSections = getSortedSections();
 *
 * // Toggle row collapse
 * <button onClick={() => toggleCollapse('published-worked')}>
 *   {section.collapsed ? 'Expand' : 'Collapse'}
 * </button>
 *
 * // Reorder on drag
 * <DndContext onDragEnd={(e) => reorder(e.active.id, e.over.id)}>
 *   {visibleSections.map(section => <Section key={section.id} {...section} />)}
 * </DndContext>
 * ```
 */
export const useDashboardStore = (teamId: number | null) => {
  const cacheKey = `dashboard-${teamId || 'default'}`;

  // Return cached store if exists
  if (!storeCache.has(cacheKey)) {
    storeCache.set(cacheKey, createDashboardStore(teamId));
  }

  return storeCache.get(cacheKey)!();
};
